import React, { useState } from "react";
import { InputNumber, Select, theme, Typography } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import { PartnerBucketGroupPrice } from "../../../../../interfaces/partnerDeal.interface";

const { Text, Title } = Typography;

export type PricingBucketRow = {
  key: string;
  label: string;
  floor: PartnerBucketGroupPrice;
  value: PartnerBucketGroupPrice;
  max: number;
};

interface PricingBucketsStepProps {
  sectionTitle: string;
  sectionSubtitle: string;
  rangeLabel: string;
  rangeHelp: string;
  rangeOptions: { value: string; label: string }[];
  selectedRangeId?: string;
  rangeDisabled?: boolean;
  rows: PricingBucketRow[];
  onRangeChange?: (rangeId: string) => void;
  onValueChange: (key: string, value: number | null) => void;
}

const formatPrice = (
  value: number | undefined,
  valueType: PartnerBucketGroupPrice["valueType"]
) => {
  if (typeof value !== "number") return "—";
  return valueType === "Fixed"
    ? `€${value.toFixed(2)}`
    : `${value.toFixed(2)}%`;
};

const formatInputValue = (
  value: number,
  valueType: PartnerBucketGroupPrice["valueType"]
) => (valueType === "Fixed" ? value.toFixed(2) : value.toFixed(2));

const formatDelta = (
  delta: number,
  valueType: PartnerBucketGroupPrice["valueType"]
) => {
  const sign = delta >= 0 ? "+" : "";
  return valueType === "Fixed"
    ? `${sign}€${delta.toFixed(2)}`
    : `${sign}${delta.toFixed(2)}%`;
};

const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

// Keep a marker label inside the bar bounds: left-align near the start,
// right-align near the end, otherwise center it under the marker.
const anchorTransform = (pos: number) => {
  if (pos < 15) return "translateX(0)";
  if (pos > 85) return "translateX(-100%)";
  return "translateX(-50%)";
};

export default function PricingBucketsStep({
  sectionTitle,
  sectionSubtitle,
  rangeLabel,
  rangeHelp,
  rangeOptions,
  selectedRangeId,
  rangeDisabled,
  rows,
  onRangeChange,
  onValueChange,
}: PricingBucketsStepProps) {
  const { token } = theme.useToken();
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  // Toggling the whole breakdown clears per-row overrides so "Show breakdown"
  // always re-expands every row (a stale per-row `false` used to make it look dead).
  const toggleBreakdown = () => {
    setShowBreakdown((prev) => !prev);
    setExpandedRows({});
  };

  return (
    <div style={{ margin: "-4px -24px -16px" }}>
      <div style={{ padding: "24px 24px 18px" }}>
        <Title level={5} style={{ margin: 0 }}>
          {rangeLabel}
        </Title>
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          {rangeHelp}
        </Text>
        <Select
          value={selectedRangeId}
          options={rangeOptions}
          disabled={rangeDisabled}
          onChange={onRangeChange}
          style={{ width: 360, maxWidth: "100%" }}
        />
      </div>

      <div
        style={{
          margin: "0 24px 20px",
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: 14,
          overflow: "hidden",
          background: token.colorBgContainer,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 22px",
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <div>
            <Title level={5} style={{ margin: 0 }}>
              {sectionTitle}
            </Title>
            <Text type="secondary">{sectionSubtitle}</Text>
          </div>
          <button
            type="button"
            onClick={toggleBreakdown}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 7,
              minWidth: 100,
              height: 26,
              padding: "0 10px",
              borderRadius: 8,
              border: `1px solid ${
                showBreakdown
                  ? token.colorPrimaryBorder
                  : token.colorBorderSecondary
              }`,
              color: showBreakdown
                ? token.colorPrimary
                : token.colorTextSecondary,
              background: showBreakdown
                ? token.colorPrimaryBg
                : token.colorBgContainer,
              cursor: "pointer",
              fontWeight: 500,
              fontSize: 11,
              lineHeight: 1,
              boxShadow: "none",
            }}
          >
            <MenuOutlined style={{ fontSize: 11 }} />
            {showBreakdown ? "Hide breakdown" : "Show breakdown"}
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(180px, 1.4fr) 140px 160px 140px 42px",
            gap: 18,
            padding: "10px 22px",
            background: token.colorFillAlter,
            color: token.colorTextTertiary,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: "uppercase",
          }}
        >
          <span>Bucket</span>
          <span style={{ color: token.colorPrimary }}>• Klearly floor</span>
          <span style={{ color: token.colorText }}>Partner default</span>
          <span>Max</span>
          <span />
        </div>

        {rows.map((row) => (
          <BucketRow
            key={row.key}
            row={row}
            expanded={showBreakdown && expandedRows[row.key] !== false}
            onToggle={() => {
              setShowBreakdown(true);
              setExpandedRows((prev) => ({
                ...prev,
                [row.key]: showBreakdown ? prev[row.key] === false : true,
              }));
            }}
            onValueChange={onValueChange}
          />
        ))}
      </div>
    </div>
  );
}

function BucketRow({
  row,
  expanded,
  onToggle,
  onValueChange,
}: {
  row: PricingBucketRow;
  expanded: boolean;
  onToggle: () => void;
  onValueChange: (key: string, value: number | null) => void;
}) {
  const { token } = theme.useToken();
  const valueType = row.value.valueType;
  const value = row.value.value;
  const floor = row.floor.value;
  const max = row.max;
  const belowFloor = value < floor;
  const aboveMax = value > max;
  const hasError = belowFloor || aboveMax;
  const markup = value - floor;
  const maxBase = max > 0 ? max : Math.max(value, floor, 1);
  const floorPos = clampPercent((floor / maxBase) * 100);
  const valuePos = clampPercent((value / maxBase) * 100);
  // When the markers nearly coincide (markup ≈ 0) the two labels would stack
  // on top of each other, so split them left/right around the shared point.
  const labelsCollide = Math.abs(valuePos - floorPos) < 14;
  return (
    <div
      style={{
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        padding: "12px 22px",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(180px, 1.4fr) 140px 160px 140px 42px",
          gap: 18,
          alignItems: "start",
        }}
      >
        <div>
          <Text strong>{row.label}</Text>
          {hasError && (
            <Text
              type="danger"
              style={{ display: "block", marginTop: 6, fontSize: 12 }}
            >
              {aboveMax
                ? "Above max price"
                : `${formatDelta(value - floor, valueType)} below floor`}
            </Text>
          )}
        </div>

        <InputNumber
          controls={false}
          disabled
          value={Number(formatInputValue(floor, valueType))}
          addonBefore={valueType === "Fixed" ? "€" : undefined}
          addonAfter={valueType === "Percentage" ? "%" : undefined}
          style={{ width: "100%" }}
        />

        <div>
          <InputNumber
            controls={false}
            value={value}
            status={hasError ? "error" : undefined}
            precision={2}
            addonBefore={valueType === "Fixed" ? "€" : undefined}
            addonAfter={valueType === "Percentage" ? "%" : undefined}
            style={{ width: "100%" }}
            onChange={(next) => onValueChange(row.key, next)}
          />
          <Text
            type={hasError ? "danger" : "secondary"}
            style={{
              display: "block",
              marginTop: 6,
              color: hasError ? token.colorError : token.colorPrimary,
              fontSize: 12,
            }}
          >
            {formatDelta(markup, valueType)} markup
            {!hasError && floor > 0 && (
              <span style={{ color: token.colorTextTertiary }}>
                {" "}
                · {Math.round((markup / floor) * 100)}%
              </span>
            )}
          </Text>
        </div>

        <InputNumber
          controls={false}
          disabled
          value={Number(formatInputValue(max, valueType))}
          addonBefore={valueType === "Fixed" ? "€" : undefined}
          addonAfter={valueType === "Percentage" ? "%" : undefined}
          style={{ width: "100%" }}
        />

        <button
          type="button"
          onClick={onToggle}
          title={expanded ? "Hide split breakdown" : "Show split breakdown"}
          aria-label="Toggle split breakdown"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
            padding: 0,
            borderRadius: 6,
            border: "1px solid transparent",
            color: expanded ? token.colorPrimary : token.colorTextTertiary,
            background: expanded ? token.colorPrimaryBg : "transparent",
            cursor: "pointer",
            transition: "0.15s",
          }}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            style={{
              transform: expanded ? "none" : "rotate(180deg)",
              transition: "transform 0.15s",
            }}
          >
            <path
              d="M3 7.5L6 4.5L9 7.5"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {expanded && (
        <div style={{ marginTop: 10, paddingLeft: 0 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              color: token.colorTextTertiary,
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            <span style={{ color: token.colorPrimary }}>
              ■ KLEARLY FLOOR {formatPrice(floor, valueType)}
            </span>
            <span>MAX CAP {formatPrice(max, valueType)}</span>
          </div>
          <div style={{ position: "relative", height: 12 }}>
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: 0,
                right: 0,
                height: 7,
                transform: "translateY(-50%)",
                borderRadius: 8,
                background: token.colorFillSecondary,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${floorPos}%`,
                  background:
                    "linear-gradient(90deg, #3867ff 0%, #8b3dff 62%, #cb22ff 100%)",
                  transition: "width 0.3s",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  left: `${floorPos}%`,
                  top: 0,
                  bottom: 0,
                  width: `${Math.max(0, valuePos - floorPos)}%`,
                  background:
                    "repeating-linear-gradient(135deg, rgb(217, 208, 255) 0px, rgb(217, 208, 255) 4px, rgb(201, 189, 255) 4px, rgb(201, 189, 255) 8px)",
                  transition: "width 0.3s",
                }}
              />
            </div>
            <span
              style={{
                position: "absolute",
                left: `${floorPos}%`,
                top: "50%",
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: token.colorPrimary,
                transform: "translate(-50%, -50%)",
                border: `2px solid ${token.colorBgContainer}`,
              }}
            />
            <span
              style={{
                position: "absolute",
                left: `${valuePos}%`,
                top: "50%",
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: token.colorText,
                transform: "translate(-50%, -50%)",
                border: `2px solid ${token.colorBgContainer}`,
              }}
            />
          </div>
          <div style={{ position: "relative", height: 38, marginTop: 6 }}>
            {labelsCollide ? (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: `${(floorPos + valuePos) / 2}%`,
                  transform: anchorTransform((floorPos + valuePos) / 2),
                  display: "flex",
                  gap: 18,
                  whiteSpace: "nowrap",
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: 1.4,
                }}
              >
                <span style={{ color: token.colorPrimary }}>
                  PARTNER MARKUP
                  <br />
                  {formatDelta(markup, valueType)}
                </span>
                <span style={{ color: token.colorTextSecondary }}>
                  MERCHANT PAYS
                  <br />
                  <span style={{ color: token.colorText, fontSize: 13 }}>
                    {formatPrice(value, valueType)}
                  </span>
                </span>
              </div>
            ) : (
              <>
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: `${floorPos}%`,
                    transform: anchorTransform(floorPos),
                    whiteSpace: "nowrap",
                    fontSize: 11,
                    fontWeight: 700,
                    lineHeight: 1.4,
                    color: token.colorPrimary,
                  }}
                >
                  PARTNER MARKUP
                  <br />
                  {formatDelta(markup, valueType)}
                </div>
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: `${valuePos}%`,
                    transform: anchorTransform(valuePos),
                    whiteSpace: "nowrap",
                    fontSize: 11,
                    fontWeight: 700,
                    lineHeight: 1.4,
                    color: token.colorTextSecondary,
                    textAlign: "right",
                  }}
                >
                  MERCHANT PAYS
                  <br />
                  <span style={{ color: token.colorText, fontSize: 13 }}>
                    {formatPrice(value, valueType)}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
